import { useState } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Eye, EyeOff, IdCard, Lock, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function StudentLogin() {
  const navigate = useNavigate();
  const [studentId, setStudentId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/auth/student/login`,
        { student_id: studentId.trim(), password },
        { withCredentials: true },
      );
      if (res.data.must_change_password) {
        navigate('/change-password', {
          state: { change_token: res.data.change_token, student_id: res.data.student_id },
          replace: true,
        });
      } else {
        window.location.href = '/';
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string' ? detail : detail?.message || 'Login failed. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-green-50 px-4">
      <Card className="w-full max-w-md p-8 shadow-xl border border-slate-200">
        <button
          onClick={() => navigate('/login')}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-4"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="flex flex-col items-center text-center mb-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center mb-4 shadow-lg">
            <svg viewBox="0 0 24 24" className="w-11 h-11 text-yellow-400 fill-current">
              <path d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4zm0 2.3l6 3v4.7c0 4.4-2.9 8.6-6 9.9-3.1-1.3-6-5.5-6-9.9V7.3l6-3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">Student Sign In</h1>
          <p className="text-slate-500 text-sm mt-1">Use your student ID to sign in</p>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="sid" className="text-sm text-slate-700">Student ID</Label>
            <div className="relative mt-1">
              <IdCard className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                id="sid"
                type="text"
                autoComplete="username"
                placeholder="e.g. 100001"
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                className="pl-9 h-11"
                required
              />
            </div>
          </div>
          <div>
            <Label htmlFor="pw" className="text-sm text-slate-700">Password</Label>
            <div className="relative mt-1">
              <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                id="pw"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-9 pr-9 h-11"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-60"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-6 leading-relaxed">
          First time signing in? Use your student ID and the password given by your teacher. You&apos;ll be asked to set a new one.
        </p>
      </Card>
    </div>
  );
}
